import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle, AlertCircle, Info, Plane, FileText, CheckSquare, Square, RefreshCw, Calendar, Lock, Search, Eye, X, Trash2 } from "lucide-react";
import DraftInvoicePreview from "../components/invoice/DraftInvoicePreview";

const InvoiceCreateInternationalPage = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // STATE TANGGAL CUSTOM (WAJIB DIISI)
  const [uploadDate, setUploadDate] = useState("");

  const [drafts, setDrafts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewGroups, setPreviewGroups] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  const [editingWeightId, setEditingWeightId] = useState(null);
  const [editWeightValue, setEditWeightValue] = useState("");
  const [isSavingWeight, setIsSavingWeight] = useState(false);
  
  // STATE BARU UNTUK FILTER PENCARIAN
  const [searchQuery, setSearchQuery] = useState("");

  const API_URL = "/api";
  const formatPriceInput = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };
  const parsePriceInput = (val) => {
    const clean = val.replace(/\./g, "");
    return clean === "" ? 0 : parseFloat(clean);
  };
  const startEditPrice = (draft, e) => {
    e.stopPropagation();
    setEditingPriceId(draft.id);
    setEditPriceValue(formatPriceInput(draft.base_price || 0));
  };
  const cancelEditPrice = (e) => {
    if (e) e.stopPropagation();
    setEditingPriceId(null);
    setEditPriceValue("");
  };
  const saveEditPrice = async (draftId, e) => {
    if (e) e.stopPropagation();
    const newPrice = parsePriceInput(editPriceValue);
    if (newPrice < 0) { toast.error("Harga tidak boleh negatif"); return; }
    setIsSavingPrice(true);
    try {
      await axios.put(`${API_URL}/invoices/draft/${draftId}/price`, { base_price: newPrice });
      setDrafts(prev => prev.map(d => d.id === draftId ? { ...d, base_price: newPrice } : d));
      toast.success("Harga berhasil diperbarui");
      setEditingPriceId(null);
      setEditPriceValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui harga");
    } finally {
      setIsSavingPrice(false);
    }
  };
  const handleDeleteDraft = async (draftId, awb) => {
    if (!window.confirm(`Hapus draft AWB ${awb}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await axios.delete(`${API_URL}/invoices/draft/${draftId}`);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      setSelectedIds(prev => prev.filter(id => id !== draftId));
      toast.success(`Draft AWB ${awb} berhasil dihapus`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menghapus draft");
    }
  };
  const startEditWeight = (draft, e) => {
    e.stopPropagation();
    setEditingWeightId(draft.id);
    setEditWeightValue(String(draft.weight_kg ?? ""));
  };
  const cancelEditWeight = (e) => {
    if (e) e.stopPropagation();
    setEditingWeightId(null);
    setEditWeightValue("");
  };
  const saveEditWeight = async (draftId, e) => {
    if (e) e.stopPropagation();
    const newWeight = parseFloat(editWeightValue.replace(',', '.'));
    if (isNaN(newWeight) || newWeight < 0) { toast.error("Weight tidak valid"); return; }
    setIsSavingWeight(true);
    try {
      await axios.put(`${API_URL}/invoices/draft/${draftId}/weight`, { weight_kg: newWeight });
      setDrafts(prev => prev.map(d => d.id === draftId ? { ...d, weight_kg: newWeight } : d));
      toast.success("Weight berhasil diperbarui");
      setEditingWeightId(null);
      setEditWeightValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui weight");
    } finally {
      setIsSavingWeight(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setIsLoadingDrafts(true);
    try {
      const res = await axios.get(`${API_URL}/invoices/draft/export`);
      setDrafts(res.data);
    } catch (error) {
      toast.error("Gagal mengambil data draft invoice");
    } finally {
      setIsLoadingDrafts(false);
    }
  };

  // PENCEGAH KLIK JIKA TANGGAL KOSONG
  const handleUploadClick = (e) => {
      if (!uploadDate) {
          e.preventDefault(); // Menggagalkan jendela file terbuka
          toast.error("STOP! Anda wajib mengisi Tanggal Invoice terlebih dahulu!");
      }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error("Harap upload file Excel (.xlsx / .xls)");
        return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("custom_date", uploadDate); // Tanggal pasti terkirim karena sudah diwajibkan

    setIsUploading(true);
    setUploadResult(null);
    const loadToast = toast.loading("Sedang memproses tagihan & menghitung harga...");

    try {
      const res = await axios.post(`${API_URL}/invoices/upload/international`, formData);
      toast.dismiss(loadToast);
      toast.success("Berhasil!");
      setUploadResult({ success: true, message: `Berhasil memproses ${res.data.invoice_count} resi.` });
      
      fetchDrafts();
    } catch (error) {
      toast.dismiss(loadToast);
      const errorMsg = error.response?.data?.detail || "Terjadi kesalahan saat upload.";
      toast.error("Gagal Upload: " + errorMsg);
      setUploadResult({ success: false, message: errorMsg });
    } finally {
      setIsUploading(false);
      e.target.value = null; 
    }
  };

  // LOGIKA PENCARIAN & FILTERING DATA
  const filteredDrafts = useMemo(() => {
    if (!searchQuery) return drafts;
    const lowerQuery = searchQuery.toLowerCase();
    
    return drafts.filter(draft => {
      const awbMatch = draft.awb?.toLowerCase().includes(lowerQuery);
      const custIdMatch = draft.customer_id?.toLowerCase().includes(lowerQuery);
      const custNameMatch = draft.customer_name?.toLowerCase().includes(lowerQuery);
      
      return awbMatch || custIdMatch || custNameMatch;
    });
  }, [drafts, searchQuery]);

  const handleSelectAll = () => {
    // Select All sekarang hanya men-select data yang sedang TERFILTER di layar
    if (selectedIds.length === filteredDrafts.length && filteredDrafts.length > 0) {
      setSelectedIds([]); 
    } else {
      setSelectedIds(filteredDrafts.map(d => d.id)); 
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handlePreviewSelected = async () => {
    if (selectedIds.length === 0) { toast.warning("Pilih minimal 1 resi untuk preview"); return; }
    setIsLoadingPreview(true);
    const loadToast = toast.loading("Membuat preview draft...");
    try {
        const res = await axios.post(`${API_URL}/invoices/preview`, { invoice_ids: selectedIds, invoice_date: uploadDate || null });
        setPreviewGroups(res.data.groups || []);
        setShowPreview(true);
        toast.dismiss(loadToast);
    } catch (error) {
        toast.error("Gagal membuat preview", { id: loadToast });
    } finally {
        setIsLoadingPreview(false);
    }
  };
  const handleGenerateSelected = async () => {
    if (selectedIds.length === 0) {
        toast.warning("Pilih minimal 1 resi untuk di-generate");
        return;
    }

    if (!window.confirm(`Yakin ingin men-generate ${selectedIds.length} resi menjadi Invoice Resmi?`)) return;

    setIsGenerating(true);
    const loadToast = toast.loading("Sedang men-generate Invoice...");

    try {
        const res = await axios.post(`${API_URL}/invoices/generate`, {
             invoice_ids: selectedIds,
             invoice_date: uploadDate
        });
        
        toast.dismiss(loadToast);
        toast.success(`Berhasil membuat ${res.data.invoice_count} Invoice Baru!`);
        
        setSelectedIds([]); 
        fetchDrafts();      
    } catch (error) {
        toast.dismiss(loadToast);
        toast.error("Gagal generate invoice: " + (error.response?.data?.detail || "Error sistem"));
    } finally {
        setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      
      <div className="flex items-center gap-3">
        <div className="p-3 bg-blue-100 text-blue-700 rounded-xl">
            <Plane size={32} />
        </div>
        <div>
            <h1 className="text-2xl font-bold text-gray-900">Super Page: International</h1>
            <p className="text-gray-500">Upload Excel, cek list tagihan, dan Generate secara Bulky / Satuan.</p>
        </div>
      </div>

      {/* --- BAGIAN ATAS: UPLOAD AREA --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
            
            {/* AREA INPUT TANGGAL (DIUBAH JADI MERAH & WAJIB) */}
            <div className={`p-4 border-2 rounded-xl shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4 transition-colors ${!uploadDate ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
                <div>
                    <h3 className={`font-bold flex items-center gap-2 ${!uploadDate ? 'text-red-800' : 'text-green-800'}`}>
                        <Calendar size={18} className={!uploadDate ? 'text-red-600' : 'text-green-600'}/> 
                        Tanggal Invoice (WAJIB DIISI)
                    </h3>
                    <p className={`text-xs mt-1 ${!uploadDate ? 'text-red-600' : 'text-green-700'}`}>
                        {!uploadDate ? "Silakan isi tanggal sebelum melakukan upload Excel." : "Tanggal sudah diset, silakan upload file Excel Anda."}
                    </p>
                </div>
                <input 
                    type="date" 
                    className="p-2 border-2 border-gray-300 rounded-lg text-sm outline-none focus:border-blue-500 font-bold cursor-pointer bg-white"
                    value={uploadDate}
                    onChange={(e) => setUploadDate(e.target.value)}
                />
            </div>

            {/* KOTAK UPLOAD EXCEL (TERKUNCI JIKA TANGGAL KOSONG) */}
            <div className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center text-center transition-all h-56 relative ${!uploadDate ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : (isUploading ? 'bg-blue-50 border-blue-300' : 'bg-white border-blue-400 hover:bg-blue-50 hover:border-blue-500')}`}>
                {isUploading ? (
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="font-bold text-gray-700">Sedang Memproses Excel...</p>
                    </div>
                ) : !uploadDate ? (
                    <div className="flex flex-col items-center opacity-50">
                        <div className="bg-gray-200 p-4 rounded-full mb-3"><Lock className="text-gray-500 w-8 h-8" /></div>
                        <h3 className="text-lg font-bold text-gray-700">Area Upload Terkunci</h3>
                        <p className="text-sm text-gray-500 mt-1">Pilih Tanggal Invoice di atas untuk membuka</p>
                        {/* Input file ditimpa dengan onClick pencegah */}
                        <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-not-allowed z-10" onClick={handleUploadClick} />
                    </div>
                ) : (
                    <>
                        <input type="file" accept=".xlsx, .xls" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onClick={handleUploadClick} onChange={handleUpload} disabled={isUploading} />
                        <div className="bg-blue-100 p-4 rounded-full mb-3"><Upload className="text-blue-600 w-8 h-8" /></div>
                        <h3 className="text-lg font-bold text-blue-900">Klik atau Tarik File Excel ke Sini</h3>
                        <p className="text-sm text-gray-500 mt-1 font-mono">Format: .xlsx</p>
                    </>
                )}
            </div>

            {uploadResult && (
                <div className={`p-4 rounded-xl border flex items-start gap-3 ${uploadResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                    {uploadResult.success ? <CheckCircle className="shrink-0 mt-0.5" /> : <AlertCircle className="shrink-0 mt-0.5" />}
                    <div>
                        <h4 className="font-bold">{uploadResult.success ? "Upload Sukses!" : "Upload Gagal"}</h4>
                        <p className="text-sm">{uploadResult.message}</p>
                    </div>
                </div>
            )}
        </div>

        <div className="md:col-span-1 bg-white p-5 rounded-xl border shadow-sm text-sm">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-3"><Info size={18} className="text-blue-600" /> Panduan Excel</h3>
            <ul className="space-y-2 text-gray-700">
                <li><span className="font-mono bg-gray-100 px-1 rounded">A</span> NO. CWB (AWB)</li>
                <li><span className="font-mono bg-yellow-100 px-1 rounded font-bold">C</span> DESTINATION</li>
                <li><span className="font-mono bg-gray-100 px-1 rounded">D</span> Date (Tgl Pengiriman)</li>
                <li><span className="font-mono bg-yellow-100 px-1 rounded font-bold">E</span> CD_CUST</li>
                <li><span className="font-mono bg-yellow-100 px-1 rounded font-bold">H</span> CWB_SVCE</li>
                <li><span className="font-mono bg-yellow-100 px-1 rounded font-bold">J</span> WEIGHT (Gram)</li>
            </ul>
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* --- BAGIAN BAWAH: TABEL DRAFT & BULK ACTIONS --- */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-50 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
                <FileText className="text-gray-500" />
                <h2 className="font-bold text-gray-800 text-lg">Draft Tagihan ({filteredDrafts.length})</h2>
                {selectedIds.length > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded-full">
                        {selectedIds.length} Dipilih
                    </span>
                )}
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                {/* SEARCH INPUT BARU */}
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Cari AWB, ID, Nama..." 
                        className="pl-9 w-full p-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 outline-none"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <button
                    onClick={handlePreviewSelected}
                    disabled={selectedIds.length === 0 || isLoadingPreview}
                    className={`flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-bold text-white transition-all w-full sm:w-auto ${selectedIds.length === 0 || isLoadingPreview ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-md'}`}
                >
                    {isLoadingPreview ? <RefreshCw className="animate-spin" size={18} /> : <Eye size={18} />}
                    Preview Draft
                </button>
                <button 
                    onClick={handleGenerateSelected}
                    disabled={selectedIds.length === 0 || isGenerating}
                    className={`flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-bold text-white transition-all w-full sm:w-auto ${selectedIds.length === 0 || isGenerating ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 shadow-md'}`}
                >
                    {isGenerating ? <RefreshCw className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                    Generate Invoice
                </button>
            </div>
        </div>

        {showPreview && (
          <div className="fixed inset-0 z-[100] bg-black/60 flex flex-col" onClick={() => setShowPreview(false)}>
            <div className="flex items-center justify-between p-4 bg-white shadow" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-gray-800">Preview Draft Invoice ({previewGroups.length} invoice)</h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-red-600"><X size={24} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-gray-100" onClick={(e) => e.stopPropagation()}>
              <DraftInvoicePreview groups={previewGroups} />
            </div>
          </div>
        )}
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm">
                    <tr>
                        <th className="px-4 py-3 border-b w-10 text-center">
                            <button onClick={handleSelectAll} className="text-gray-500 hover:text-blue-600">
                                {selectedIds.length === filteredDrafts.length && filteredDrafts.length > 0 ? <CheckSquare size={20} className="text-blue-600" /> : <Square size={20} />}
                            </button>
                        </th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b">AWB</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b">Tgl Kirim (AWB)</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b">Customer</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b">Dest</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b text-right">Weight</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b text-right">Base Price</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 border-b text-center">Aksi</th>
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {isLoadingDrafts ? (
                        <tr><td colSpan="7" className="p-8 text-center text-gray-500">Memuat data...</td></tr>
                    ) : filteredDrafts.length === 0 ? (
                        <tr><td colSpan="7" className="p-8 text-center text-gray-500">Belum ada tagihan draft atau tidak ada hasil pencarian.</td></tr>
                    ) : (
                        filteredDrafts.map((draft) => (
                            <tr key={draft.id} className={`hover:bg-gray-50 cursor-pointer ${selectedIds.includes(draft.id) ? 'bg-blue-50/50' : ''}`} onClick={() => handleSelectOne(draft.id)}>
                                <td className="px-4 py-3 text-center">
                                    {selectedIds.includes(draft.id) ? <CheckSquare size={18} className="text-blue-600 mx-auto" /> : <Square size={18} className="text-gray-400 mx-auto" />}
                                </td>
                                <td className="px-4 py-3 font-medium text-blue-600">{draft.awb}</td>
                                <td className="px-4 py-3 text-gray-600">
                                    {draft.date ? new Date(draft.date).toLocaleDateString('id-ID') : '-'}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-bold text-gray-800">{draft.customer_id}</div>
                                    <div className="text-xs text-gray-500 truncate w-48">{draft.customer_name}</div>
                                </td>
                                <td className="px-4 py-3 font-mono text-gray-600">{draft.destination}</td>
                                <td className="px-4 py-3 text-right">
                                    {editingWeightId === draft.id ? (
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="text"
                                                autoFocus
                                                className="w-16 p-1 text-right border border-blue-400 rounded text-sm outline-none focus:ring-1 focus:ring-blue-400"
                                                value={editWeightValue}
                                                onChange={(e) => setEditWeightValue(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') saveEditWeight(draft.id, e); if (e.key === 'Escape') cancelEditWeight(e); }}
                                                disabled={isSavingWeight}
                                            />
                                            <span className="text-gray-500 text-xs">kg</span>
                                            <button onClick={(e) => saveEditWeight(draft.id, e)} disabled={isSavingWeight} className="text-green-600 hover:text-green-800 text-xs font-bold px-1">✓</button>
                                            <button onClick={(e) => cancelEditWeight(e)} disabled={isSavingWeight} className="text-gray-400 hover:text-red-500 text-xs font-bold px-1">✕</button>
                                        </div>
                                    ) : (
                                        <span
                                            className="cursor-pointer hover:bg-yellow-50 hover:underline decoration-dotted px-1 rounded"
                                            title="Klik untuk edit weight"
                                            onClick={(e) => startEditWeight(draft, e)}
                                        >
                                            {draft.weight_kg} kg
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right font-mono font-medium text-gray-800">
                                    {editingPriceId === draft.id ? (
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <span className="text-gray-500 text-xs">Rp</span>
                                            <input
                                                type="text"
                                                autoFocus
                                                className="w-24 p-1 text-right border border-blue-400 rounded text-sm outline-none focus:ring-1 focus:ring-blue-400"
                                                value={editPriceValue}
                                                onChange={(e) => setEditPriceValue(formatPriceInput(parsePriceInput(e.target.value)))}
                                                onKeyDown={(e) => { if (e.key === 'Enter') saveEditPrice(draft.id, e); if (e.key === 'Escape') cancelEditPrice(e); }}
                                                disabled={isSavingPrice}
                                            />
                                            <button onClick={(e) => saveEditPrice(draft.id, e)} disabled={isSavingPrice} className="text-green-600 hover:text-green-800 text-xs font-bold px-1">✓</button>
                                            <button onClick={(e) => cancelEditPrice(e)} disabled={isSavingPrice} className="text-gray-400 hover:text-red-500 text-xs font-bold px-1">✕</button>
                                        </div>
                                    ) : (
                                        <span
                                            className="cursor-pointer hover:bg-yellow-50 hover:underline decoration-dotted px-1 rounded"
                                            title="Klik untuk edit harga"
                                            onClick={(e) => startEditPrice(draft, e)}
                                        >
                                            Rp {draft.base_price?.toLocaleString('id-ID')}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={() => handleDeleteDraft(draft.id, draft.awb)}
                                        className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition"
                                        title="Hapus draft ini"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>

      </div>

    </div>
  );
};

export default InvoiceCreateInternationalPage;